import React from "react"
import { useTranslation } from "react-i18next"

import type {
  BillPdfRecommendation,
  BillPdfRecommendationBand,
  BillPdfRecommendationCheckKey,
  BillPdfRecommendationReason,
} from "@/modules/data/services/use-bill-pdf-upload-session"

import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import type { ChipProps } from "@mui/material"
import { Box, Chip, Tooltip, Typography } from "@mui/material"

interface BillPdfRecommendationChipProps {
  band: BillPdfRecommendationBand
  recommendation?: BillPdfRecommendation
}

const BAND_CHIP_COLORS: Record<BillPdfRecommendationBand, ChipProps["color"]> =
  {
    proceed: "success",
    reject: "error",
    review: "info",
  }

const BAND_LABEL_KEYS = {
  proceed: "upload.billPdf.recommendationProceed",
  reject: "upload.billPdf.recommendationReject",
  review: "upload.billPdf.recommendationReview",
} as const satisfies Record<BillPdfRecommendationBand, string>

const CHECK_LABEL_KEYS = {
  cost_present: "upload.billPdf.recommendationCheckCost",
  line_items_reconcile: "upload.billPdf.recommendationCheckLineItems",
  meter_present: "upload.billPdf.recommendationCheckMeter",
  period_plausible: "upload.billPdf.recommendationCheckPeriod",
  provider_present: "upload.billPdf.recommendationCheckProvider",
  units_type_consistent: "upload.billPdf.recommendationCheckUnits",
  usage_present: "upload.billPdf.recommendationCheckUsage",
} as const satisfies Record<BillPdfRecommendationCheckKey, string>

const REASON_LABEL_KEYS = {
  duplicate_period: "upload.billPdf.recommendationReasonDuplicatePeriod",
  partial_parse: "upload.billPdf.recommendationReasonPartialParse",
} as const satisfies Record<BillPdfRecommendationReason, string>

const RecommendationDetails: React.FC<{
  recommendation: BillPdfRecommendation
}> = ({ recommendation }) => {
  const { t } = useTranslation("data")

  if (recommendation.unscored) {
    return (
      <Typography variant="body2">
        {t("upload.billPdf.recommendationNoDetails")}
      </Typography>
    )
  }

  return (
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {recommendation.reasons.map((reason) => (
        <Typography component="li" key={reason} variant="body2">
          {t(REASON_LABEL_KEYS[reason])}
        </Typography>
      ))}
      {recommendation.checks.map(({ key, passed }) => (
        <Box
          component="li"
          key={key}
          sx={{ alignItems: "center", display: "flex", gap: 0.5 }}
        >
          {passed ? (
            <CheckIcon
              color="success"
              fontSize="small"
              titleAccess={t("upload.billPdf.recommendationCheckPassed")}
            />
          ) : (
            <CloseIcon
              color="error"
              fontSize="small"
              titleAccess={t("upload.billPdf.recommendationCheckFailed")}
            />
          )}
          <Typography variant="body2">{t(CHECK_LABEL_KEYS[key])}</Typography>
        </Box>
      ))}
    </Box>
  )
}

/** A bill passes its recommendation to get the tooltip. The file chip does not. */
export const BillPdfRecommendationChip: React.FC<
  BillPdfRecommendationChipProps
> = ({ band, recommendation }) => {
  const { t } = useTranslation("data")
  const chip = (
    <Chip
      color={BAND_CHIP_COLORS[band]}
      label={t(BAND_LABEL_KEYS[band])}
      size="small"
      tabIndex={recommendation ? 0 : undefined}
      variant="outlined"
    />
  )

  if (!recommendation) {
    return chip
  }

  return (
    <Tooltip
      describeChild
      title={<RecommendationDetails recommendation={recommendation} />}
    >
      {chip}
    </Tooltip>
  )
}
